import { Injectable, OnInit, OnDestroy } from '@angular/core';
import { Observable, ReplaySubject, of } from 'rxjs';
import { HttpUtilService } from '@app/services/http-util.service';
import { environment } from '@env/environment';
import { ItemsService } from './items.service';
import { VersionControlService } from './version-control.service';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
/** Centralized storage and functionality to support course data items*/
export class CourseService {
  constructor(
    public http: HttpUtilService,
    private itemsService: ItemsService,
    private versionService: VersionControlService
  ) {}

  /**Stored data titles*/
  titles = {};
  /**Details about the current course*/
  courseDetails: any;
  private courseDetailsSubject = new ReplaySubject<any>(1);

  data;
  /**Current course ID*/
  _id: any;
  /**Set course ID*/
  set id(id: number) {
    console.log('Set Course', id);
    if (id !== this._id) {
      this._id = id;
      if (id) {
        this.getCourse(id);
      } else {
        this.data = null;
        this.courseDetails = null;
        this.courseDetailsSubject.next(null);
      }
    }
  }
  /**Get course ID*/
  get id(): number {
    return this._id;
  }
  /**Institution ID*/
  partnerId: string | number = null;
  /**
   * Set institution ID
   * @param partnerId ID to set
   */
  setPartnerId(partnerId: string | number) {
    this.partnerId = partnerId;
  }
  /**Get institution ID */
  getPartnerId(): string | number {
    return this.partnerId;
  }
  /**
   * Get title of the requested data item
   * @param id Data id
   * @param type Data type
   */
  getTitle(id: string | number, type?: string): string {
    if (this.titles) {
      if (type && this.titles[type]) {
        return this.titles[type][id];
      } else {
        return this.titles[id];
      }
    }
  }
  /**
   * Set the title for data item
   * @param id Id to set
   * @param title title to set
   * @param type type to set
   */
  setTitle(id: string | number, title: string, type?: string) {
    if (this.titles) {
      if (type) {
        if (!this.titles[type]) {
          this.titles[type] = {};
        }
        this.titles[type][id] = title;
      } else {
        this.titles[id] = title;
      }
    }
  }
  /**
   * Get data item from API
   * @param type type of data to get
   */
  get(type: string): Observable<any> {
    console.log('get', type);
    if (this.versionService.currentVersion) {
      if (!type.match(/version=/)) {
        if (type.match(/\?/)) {
          type += '&version=' + this.versionService.currentVersion;
        } else {
          type += '?version=' + this.versionService.currentVersion;
        }
      }
    }
    return this.http.request(
      'GET',
      environment.apiRoot + '/' + type,
      null,
      null,
      true
    );
  }
  /**
   * Gets details about a course and stores it in this.data
   * @param id ID of course
   */
  getCourse(id: number) {
    this.id = id;
    this.http
      .request(
        'GET',
        environment.apiRoot +
          '/courses/' +
          id +
          '?mode=list&output=dataonly&key_values=target_lms,title,id,lms,destination_lms,updated_in_lms,source_lms',
        null,
        null,
        true
      )
      .subscribe(c => {
        this.courseDetails = c;
        this.courseDetailsSubject.next(c);
        this.data = c.data;
        this.itemsService
          .registerItem({ id: this.id, type: 'courses' })
          .pipe(take(1))
          .subscribe(d => {
            if (this.data) {
              Object.keys(d).forEach(i => {
                this.data[i] = d[i];
              });
            }
            console.log(this.data);
          });
      });
  }

  getCourses(input) {
    return this.http.request(
      'GET',
      `${environment.apiRoot}/courses?mode=list&output=dataonly&key_values=${
        input?.keyValues || 'target_lms,title,id,lms,id'
      }${
        input?.searchParam && input?.searchTerm
          ? `&searchParam=${input?.searchParam}&searchTerm=${input?.searchTerm}`
          : ''
      }${
        input?.perPage && input?.pageNumber !== undefined
          ? `&perPage=${input?.perPage}&pageNumber=${input?.pageNumber}`
          : ''
      }
      ${
        input?.direction && input?.orderBy
          ? `&direction=${input?.direction}&orderBy=${input?.orderBy}`
          : ''
      }
      `
    );
  }
  /**
   * Create a new course data item
   * @param data Settings for the course
   * @param partnerId Institution to create the course in
   */
  createCourse(data, partnerId = null): Observable<any> {
    let newCoursePath = `${environment.apiRoot}/courses`;
    if (partnerId != null) {
      newCoursePath += `?partnerId=${partnerId}`;
    }
    return this.http.request('POST', newCoursePath, data, null, true);
  }

  getCurrentCourseDetails(): Observable<any> {
    if (this.courseDetails || !this._id) {
      return of(this.courseDetails);
    }
    return this.courseDetailsSubject.asObservable().pipe(take(1));
  }
}
